import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { uploadToSiteMedia } from "@/lib/uploadAsset";
import { Trash2, Upload, Image as ImageIcon, Video, FileText, RefreshCw, Star, StarOff } from "lucide-react";

type Asset = {
  id: string;
  role: string;
  label: string | null;
  caption: string | null;
  file_url: string;
  thumbnail_url: string | null;
  display_order: number;
  active: boolean;
  metadata: Record<string, any>;
  created_at: string;
};

const SLOTS: { role: string; title: string; description: string; folder: string; accept: string; collection?: boolean; keys?: { key: string; label: string }[] }[] = [
  {
    role: "founder_photo",
    title: "Founder Photo",
    description: "Square headshot shown in the Founder Authority section. Only the first active photo is used.",
    folder: "founder",
    accept: "image/*",
  },
  {
    role: "founder_video",
    title: "Founder Video",
    description: "Short MP4 or paste a YouTube/Vimeo URL. Only the first active video is used.",
    folder: "founder",
    accept: "video/*",
  },
  {
    role: "founder_video_poster",
    title: "Founder Video Poster",
    description: "Optional poster image shown before the founder video plays.",
    folder: "founder",
    accept: "image/*",
  },
  {
    role: "dashboard_screenshot",
    title: "Dashboard Screenshots",
    description: "Real screenshots from the operator platform. Public Dashboard Showcase only renders when at least one is added.",
    folder: "dashboard",
    accept: "image/*",
    collection: true,
    keys: [
      { key: "visitor-intelligence", label: "Visitor Intelligence" },
      { key: "lead-pipeline", label: "Lead Pipeline" },
      { key: "follow-up-center", label: "Follow-Up Center" },
      { key: "executive-dashboard", label: "Executive Dashboard" },
    ],
  },
  {
    role: "media_library",
    title: "Media Library",
    description: "PDFs, brochures, lead magnets, or any general media used elsewhere. Copy the file URL to use it.",
    folder: "library",
    accept: "*/*",
    collection: true,
  },
];

const AssetManager = () => {
  const { toast } = useToast();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadingRole, setUploadingRole] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.from("site_assets").select("*").order("display_order").order("created_at");
    if (error) toast({ title: "Failed to load assets", description: error.message, variant: "destructive" });
    setAssets((data || []) as Asset[]);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const insertAsset = async (row: Partial<Asset> & { role: string; file_url: string }) => {
    const { error } = await supabase.from("site_assets").insert([row]);
    if (error) throw error;
  };

  const handleUpload = async (slot: typeof SLOTS[number], file: File, key?: string, label?: string) => {
    setUploadingRole(slot.role);
    try {
      const url = await uploadToSiteMedia(file, slot.folder);
      const metadata: Record<string, any> = {};
      if (key) metadata.key = key;
      const lbl = label || slot.keys?.find((k) => k.key === key)?.label || file.name;
      await insertAsset({
        role: slot.role,
        file_url: url,
        label: lbl,
        active: true,
        display_order: assets.filter((a) => a.role === slot.role).length,
        metadata,
      });
      toast({ title: "Uploaded", description: lbl });
      load();
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message, variant: "destructive" });
    } finally {
      setUploadingRole(null);
    }
  };

  const handleUrlAdd = async (slot: typeof SLOTS[number], url: string) => {
    if (!url.trim()) return;
    try {
      await insertAsset({
        role: slot.role,
        file_url: url.trim(),
        label: slot.title,
        active: true,
        display_order: assets.filter((a) => a.role === slot.role).length,
        metadata: {},
      });
      toast({ title: "Saved" });
      load();
    } catch (e: any) {
      toast({ title: "Save failed", description: e.message, variant: "destructive" });
    }
  };

  const setActiveSingle = async (asset: Asset) => {
    // Deactivate others of the same role, activate this one
    await supabase.from("site_assets").update({ active: false }).eq("role", asset.role).neq("id", asset.id);
    await supabase.from("site_assets").update({ active: true }).eq("id", asset.id);
    load();
  };

  const toggleActive = async (asset: Asset) => {
    await supabase.from("site_assets").update({ active: !asset.active }).eq("id", asset.id);
    load();
  };

  const updateField = async (id: string, patch: Partial<Asset>) => {
    await supabase.from("site_assets").update(patch).eq("id", id);
    load();
  };

  const remove = async (asset: Asset) => {
    if (!confirm(`Delete "${asset.label || asset.file_url}"?`)) return;
    await supabase.from("site_assets").delete().eq("id", asset.id);
    toast({ title: "Deleted" });
    load();
  };

  const copyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    toast({ title: "URL copied" });
  };

  const cardCls = "rounded-xl border p-5";
  const cardStyle = { background: "hsl(0 0% 8%)", borderColor: "hsl(0 0% 16%)" } as React.CSSProperties;
  const textMuted = { color: "hsl(0 0% 55%)" };
  const textFg = { color: "hsl(0 0% 90%)" };
  const textDim = { color: "hsl(0 0% 45%)" };

  const renderPreview = (a: Asset) => {
    const isImg = /\.(png|jpe?g|webp|gif|svg)$/i.test(a.file_url) || a.file_url.startsWith("data:image");
    const isVid = /\.(mp4|webm|mov)$/i.test(a.file_url);
    const isEmbed = /(youtube\.com|youtu\.be|vimeo\.com)/.test(a.file_url);
    if (isImg) return <img src={a.file_url} alt={a.label || ""} className="h-20 w-20 rounded-md object-cover" loading="lazy" />;
    if (isVid) return <div className="flex h-20 w-20 items-center justify-center rounded-md bg-black/40"><Video className="h-6 w-6" style={{ color: "hsl(207 68% 60%)" }} /></div>;
    if (isEmbed) return <div className="flex h-20 w-20 items-center justify-center rounded-md bg-black/40"><Video className="h-6 w-6" style={{ color: "hsl(207 68% 60%)" }} /></div>;
    return <div className="flex h-20 w-20 items-center justify-center rounded-md bg-black/40"><FileText className="h-6 w-6" style={{ color: "hsl(207 68% 60%)" }} /></div>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold" style={textFg}>Asset Manager</h2>
          <p className="text-sm" style={textMuted}>Upload and manage every media file used across the public site.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="border" style={{ borderColor: "hsl(0 0% 20%)", color: "hsl(0 0% 70%)", background: "transparent" }}>
          <RefreshCw size={14} className="mr-1" /> Refresh
        </Button>
      </div>

      {SLOTS.map((slot) => {
        const items = assets.filter((a) => a.role === slot.role);
        return (
          <div key={slot.role} className={cardCls} style={cardStyle}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  {slot.accept.startsWith("image") ? <ImageIcon size={16} style={{ color: "hsl(207 68% 60%)" }} /> :
                   slot.accept.startsWith("video") ? <Video size={16} style={{ color: "hsl(207 68% 60%)" }} /> :
                   <FileText size={16} style={{ color: "hsl(207 68% 60%)" }} />}
                  <h3 className="font-semibold" style={textFg}>{slot.title}</h3>
                  {!slot.collection && <span className="text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5" style={{ background: "hsl(0 0% 14%)", color: "hsl(0 0% 60%)" }}>Singleton</span>}
                </div>
                <p className="mt-1 text-sm" style={textMuted}>{slot.description}</p>
              </div>
            </div>

            <UploadRow
              slot={slot}
              uploading={uploadingRole === slot.role}
              onFile={(f, k, l) => handleUpload(slot, f, k, l)}
              onUrl={(u) => handleUrlAdd(slot, u)}
            />

            {items.length > 0 && (
              <div className="mt-4 space-y-2">
                {items.map((a) => (
                  <div key={a.id} className="flex items-center gap-3 rounded-lg border p-3" style={{ borderColor: "hsl(0 0% 14%)", background: a.active ? "hsl(0 0% 10%)" : "hsl(0 0% 6%)" }}>
                    {renderPreview(a)}
                    <div className="flex-1 min-w-0 space-y-1">
                      <Input
                        value={a.label || ""}
                        onChange={(e) => setAssets((prev) => prev.map((x) => x.id === a.id ? { ...x, label: e.target.value } : x))}
                        onBlur={(e) => updateField(a.id, { label: e.target.value })}
                        placeholder="Label"
                        className="h-7 text-xs"
                        style={{ background: "hsl(0 0% 12%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }}
                      />
                      <Input
                        value={a.caption || ""}
                        onChange={(e) => setAssets((prev) => prev.map((x) => x.id === a.id ? { ...x, caption: e.target.value } : x))}
                        onBlur={(e) => updateField(a.id, { caption: e.target.value })}
                        placeholder="Caption (optional)"
                        className="h-7 text-xs"
                        style={{ background: "hsl(0 0% 12%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }}
                      />
                      <div className="flex items-center gap-3 text-[11px]" style={textDim}>
                        {slot.keys && (
                          <select
                            value={(a.metadata as any)?.key || ""}
                            onChange={(e) => updateField(a.id, { metadata: { ...a.metadata, key: e.target.value } } as any)}
                            className="h-6 rounded px-1"
                            style={{ background: "hsl(0 0% 12%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 80%)" }}
                          >
                            <option value="">, assign tab, </option>
                            {slot.keys.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
                          </select>
                        )}
                        <span>Order:</span>
                        <Input
                          type="number"
                          value={a.display_order}
                          onChange={(e) => setAssets((prev) => prev.map((x) => x.id === a.id ? { ...x, display_order: Number(e.target.value) } : x))}
                          onBlur={(e) => updateField(a.id, { display_order: Number(e.target.value) })}
                          className="h-6 w-16 text-xs"
                          style={{ background: "hsl(0 0% 12%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }}
                        />
                        <button onClick={() => copyUrl(a.file_url)} className="underline hover:text-foreground" style={{ color: "hsl(207 68% 60%)" }}>Copy URL</button>
                        <a href={a.file_url} target="_blank" rel="noopener" className="underline" style={{ color: "hsl(207 68% 60%)" }}>Open</a>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {!slot.collection ? (
                        <Button size="sm" variant="outline" onClick={() => setActiveSingle(a)}
                          className="h-7 border"
                          style={{ borderColor: a.active ? "hsl(45 80% 50%)" : "hsl(0 0% 20%)", color: a.active ? "hsl(45 80% 60%)" : "hsl(0 0% 70%)", background: "transparent" }}>
                          {a.active ? <><Star size={12} className="mr-1" /> Active</> : <><StarOff size={12} className="mr-1" /> Use this</>}
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => toggleActive(a)}
                          className="h-7 border"
                          style={{ borderColor: "hsl(0 0% 20%)", color: a.active ? "hsl(140 60% 60%)" : "hsl(0 0% 50%)", background: "transparent" }}>
                          {a.active ? "Published" : "Hidden"}
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => remove(a)}
                        className="h-7 border"
                        style={{ borderColor: "hsl(0 0% 20%)", color: "hsl(0 70% 60%)", background: "transparent" }}>
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {items.length === 0 && (
              <p className="mt-3 text-xs italic" style={textDim}>No files yet. The corresponding public section stays hidden.</p>
            )}
          </div>
        );
      })}

      {loading && <p className="text-center py-4" style={textMuted}>Loading...</p>}
    </div>
  );
};

function UploadRow({
  slot,
  uploading,
  onFile,
  onUrl,
}: {
  slot: { role: string; folder: string; accept: string; keys?: { key: string; label: string }[] };
  uploading: boolean;
  onFile: (f: File, key?: string, label?: string) => void;
  onUrl: (u: string) => void;
}) {
  const [key, setKey] = useState("");
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  return (
    <div className="mt-4 grid gap-3 md:grid-cols-2">
      <div className="rounded-lg border border-dashed p-3" style={{ borderColor: "hsl(0 0% 20%)" }}>
        <Label className="text-xs" style={{ color: "hsl(0 0% 55%)" }}>Upload file</Label>
        {slot.keys && (
          <select value={key} onChange={(e) => setKey(e.target.value)}
            className="mt-1 h-8 w-full rounded px-2 text-sm"
            style={{ background: "hsl(0 0% 12%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 80%)" }}>
            <option value="">, tab (optional), </option>
            {slot.keys.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
          </select>
        )}
        <Input placeholder="Label (optional)" value={label} onChange={(e) => setLabel(e.target.value)}
          className="mt-2 h-8 text-sm" style={{ background: "hsl(0 0% 12%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }} />
        <Input
          type="file"
          accept={slot.accept}
          disabled={uploading}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onFile(f, key || undefined, label || undefined);
            e.target.value = "";
            setLabel(""); setKey("");
          }}
          className="mt-2 h-8 text-sm"
          style={{ background: "hsl(0 0% 12%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }}
        />
        {uploading && <p className="mt-1 text-xs" style={{ color: "hsl(207 68% 60%)" }}><Upload size={12} className="mr-1 inline" /> Uploading…</p>}
      </div>
      <div className="rounded-lg border border-dashed p-3" style={{ borderColor: "hsl(0 0% 20%)" }}>
        <Label className="text-xs" style={{ color: "hsl(0 0% 55%)" }}>Or paste a URL (YouTube/Vimeo/external)</Label>
        <div className="mt-2 flex gap-2">
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…"
            className="h-8 text-sm" style={{ background: "hsl(0 0% 12%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }} />
          <Button size="sm" onClick={() => { onUrl(url); setUrl(""); }}
            style={{ background: "hsl(207 68% 50%)", color: "hsl(0 0% 100%)" }}>Add</Button>
        </div>
      </div>
    </div>
  );
}

export default AssetManager;
